/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { appendObjectValuesToFormData } from '@veone/ngx-utilities/form';
import { Observable, map, shareReplay } from 'rxjs';
import {
  Video,
  VideoListParams,
  Pagination,
  VideoCreationParams,
  VideoUpdateParams,
  KalturaVideoListParams,
  KalturaVideo,
  KalturaThumbnail
} from '~core/models/core.models';
import { ApiConstantsService } from './api-constants.service';
import { SKIP_ERROR_INTERCEPTOR } from '~core/constants/global.constants';

@Injectable()
export class VideoService {
  private _httpClient = inject(HttpClient);
  private _apiConstantsService = inject(ApiConstantsService);

  read(videoId: string): Observable<Video> {
    return this._httpClient.get<Video>(
      `${this._apiConstantsService.videoUrl}/${videoId}`
    );
  }

  list(params?: VideoListParams): Observable<Pagination<Video>> {
    return this._httpClient
      .get<Pagination<Video>>(this._apiConstantsService.videoUrl, {
        params: {
          ...params,
          sort: params?.sort || 'createdAt',
          desc: params?.desc || 'createdAt'
        } as Record<string, any>
      })
      .pipe(shareReplay());
  }

  create(
    data: VideoCreationParams,
    skipErrorInterceptor?: boolean
  ): Observable<Video> {
    let body: Record<string, unknown> | FormData = data;
    const thumbnail = data.videos[0].thumbnail;

    if (thumbnail && typeof thumbnail === 'object') {
      const formData = new FormData();
      appendObjectValuesToFormData(formData, data);
      body = formData;
    }

    return this._httpClient.post<Video>(
      this._apiConstantsService.videoUrl,
      body,
      {
        ...(skipErrorInterceptor && {
          context: new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true)
        })
      }
    );
  }

  update(videoId: string, data: VideoUpdateParams): Observable<Video> {
    return this._httpClient.put<Video>(
      `${this._apiConstantsService.contentUrl}/${videoId}`,
      data
    );
  }

  updateWithFormData(
    videoId: string,
    data: VideoUpdateParams
  ): Observable<Video> {
    let formData: VideoUpdateParams | FormData = data;
    if (data.thumbnail) {
      formData = new FormData();
      appendObjectValuesToFormData(formData, data);
    }
    return this._httpClient.post<Video>(
      `${this._apiConstantsService.contentUrl}/${videoId}`,
      formData
    );
  }

  delete(videoId: string): Observable<unknown> {
    return this._httpClient.delete(
      `${this._apiConstantsService.contentUrl}/${videoId}`
    );
  }

  listKalturaVideos(
    params?: KalturaVideoListParams
  ): Observable<Pagination<KalturaVideo>> {
    return this._httpClient.get<Pagination<KalturaVideo>>(
      this._apiConstantsService.kalturaVideoUrl,
      {
        params: {
          ...params,
          sort: params?.sort || 'createdAt',
          desc: params?.desc || 'createdAt'
        } as Record<string, any>
      }
    );
  }

  listKalturaThumbnail(kalturaVideoId: string): Observable<KalturaThumbnail[]> {
    return this._httpClient
      .get<{
        items: KalturaThumbnail[];
      }>(
        `${this._apiConstantsService.kalturaVideoUrl}/${kalturaVideoId}/thumbnails`
      )
      .pipe(map(res => res.items));
  }

  deleteTags(videoId: string, tagIds: string[]): Observable<unknown> {
    return this._httpClient.delete<unknown>(
      `${this._apiConstantsService.videoUrl}/${videoId}/tags`,
      {
        body: {
          tags: tagIds
        }
      }
    );
  }

  deleteBadges(videoId: string, badgeIds: string[]): Observable<unknown> {
    return this._httpClient.delete<unknown>(
      `${this._apiConstantsService.videoUrl}/${videoId}/badges`,
      {
        body: {
          badges: badgeIds
        }
      }
    );
  }
}
